import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MagnifyingGlassIcon,
  UserCircleIcon,
  XMarkIcon,
  InformationCircleIcon,
  FunnelIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/24/solid';
import useAuthStore from '../store/authStore';
import NotificationCenter from './NotificationCenter';
import { statusLabels, getStatusStyle } from '../config/statusColors';
import useScrollDirection from '../hooks/useScrollDirection';

const PageHeader = ({ 
  title, 
  totalCount, 
  filters,
  setFilters,
  searchInput,
  setSearchInput,
  setSearchQuery,
  showCreateButton = false,
  isHome = false,
  showFilters: externalShowFilters,
  setShowFilters: externalSetShowFilters
}) => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [showSearch, setShowSearch] = useState(false);
  const [internalShowFilters, setInternalShowFilters] = useState(false);
  
  // 외부에서 제어하면 외부 상태 사용, 아니면 내부 상태 사용
  const showFilters = externalShowFilters !== undefined ? externalShowFilters : internalShowFilters;
  const setShowFilters = externalSetShowFilters || setInternalShowFilters;
  const [forceCompact, setForceCompact] = useState(false);
  const scrollContainerRef = useRef(null);
  
  // 스크롤 훅 사용
  const { isHidden } = useScrollDirection();
  const isCompact = (isHidden || forceCompact) && !isHome;

  const statusOptions = [
    { value: '', label: '전체' },
    { value: 'received', label: statusLabels.received, color: getStatusStyle('received', 'medium') },
    { value: 'review', label: statusLabels.review, color: getStatusStyle('review', 'medium') },
    { value: 'contract', label: statusLabels.contract, color: getStatusStyle('contract', 'medium') },
    { value: 'completed', label: statusLabels.completed, color: getStatusStyle('completed', 'medium') },
    { value: 'cancelled', label: statusLabels.cancelled, color: getStatusStyle('cancelled', 'medium') }
  ];



  return (
    <div 
      className={`bg-white sticky top-0 z-20 transition-all duration-300 ease-in-out ${
        isCompact && !isHome 
          ? 'compact-mode shadow-md backdrop-blur-lg bg-white/95' 
          : 'shadow-sm'
      }`}
    >
      {/* 컴팩트 모드일 때 통합 헤더 */}
      {isCompact && !isHome ? (
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/3 via-purple-500/3 to-pink-500/3"></div>
          <div className="relative px-3 py-1 backdrop-blur-sm bg-white/95 border-b border-gray-200/30 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <img 
                  src="/images/logo-geongi.png" 
                  alt="건기" 
                  className="h-5 w-auto cursor-pointer hover:scale-110 transition-transform"
                  onClick={() => navigate('/')}
                />
                <div className="flex items-center">
                  <div className="flex items-center gap-1 px-2.5 py-1 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg">
                    <div className="flex gap-0.5">
                      <div className="h-2 w-4 bg-gradient-to-r from-blue-500 to-blue-400 rounded-full opacity-90"></div>
                      <div className="h-2 w-3 bg-gradient-to-r from-purple-400 to-purple-300 rounded-full opacity-70"></div>
                      <div className="h-2 w-2 bg-gray-300 rounded-full opacity-50"></div>
                    </div>
                    <div className="h-3.5 w-px bg-gray-300/60 mx-1"></div>
                    <div className="flex items-center gap-1">
                      <div className="relative flex items-center justify-center">
                        <div className="absolute w-2 h-2 bg-blue-400 rounded-full animate-ping"></div>
                        <div className="relative w-2 h-2 bg-blue-500 rounded-full"></div>
                      </div>
                      <span className="text-xs font-bold text-gray-700">{totalCount || 0}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-1">
                <button
                  onClick={() => {
                    const newShowSearch = !showSearch;
                    setShowSearch(newShowSearch);
                    
                    // 컴팩트 모드에서 검색을 열 때 강제로 컴팩트 모드 유지
                    if (isHidden && !isHome && newShowSearch) {
                      setForceCompact(true);
                    } else if (!newShowSearch) {
                      setForceCompact(false);
                    }
                  }}
                  className={`p-1.5 rounded-lg transition-all ${
                    showSearch 
                      ? 'bg-blue-600 text-white shadow-md' 
                      : 'hover:bg-gray-100'
                  }`}
                >
                  <MagnifyingGlassIcon className={`h-5 w-5 ${
                    showSearch ? 'text-white' : 'text-gray-600'
                  }`} />
                </button>
                
                <NotificationCenter compact={true} />
                
                <button
                  onClick={() => {
                    const newShowFilters = !showFilters;
                    setShowFilters(newShowFilters);
                    
                    // 컴팩트 모드에서 필터를 열 때 강제로 컴팩트 모드 유지
                    if (isHidden && !isHome && newShowFilters) {
                      setForceCompact(true);
                    } else if (!newShowFilters) {
                      setForceCompact(false);
                    }
                    
                    if (showFilters) {
                      setFilters(prev => ({ ...prev, status: '', page: 1 }));
                      const newSearchParams = new URLSearchParams(window.location.search);
                      newSearchParams.delete('filter');
                      const newUrl = newSearchParams.toString() ? `${window.location.pathname}?${newSearchParams.toString()}` : window.location.pathname;
                      window.history.replaceState({}, '', newUrl);
                    }
                  }}
                  className={`p-1.5 rounded-lg transition-all ${
                    showFilters 
                      ? 'bg-purple-600 text-white shadow-md' 
                      : 'hover:bg-gray-100'
                  }`}
                >
                  <FunnelIcon className={`h-5 w-5 transition-transform duration-200 ${
                    showFilters ? 'rotate-180 text-white' : 'text-gray-600'
                  }`} />
                </button>
                
                <div className="w-px h-5 bg-gradient-to-b from-transparent via-gray-300 to-transparent"></div>
                
                <button
                  onClick={() => navigate(user?.user_type === 'vip_customer' ? '/vip' : '/profile')}
                  className="relative p-1.5 rounded-lg hover:bg-gray-100 transition-all group"
                >
                  <UserCircleIcon className="h-5 w-5 text-gray-600 group-hover:text-gray-800 transition-colors" />
                  {user?.user_type === 'vip_customer' && (
                    <div className="absolute -top-1 -right-1 flex items-center justify-center">
                      <div className="absolute inset-0 bg-yellow-400 rounded-full animate-ping opacity-75"></div>
                      <StarIcon className="relative h-3 w-3 text-yellow-500 drop-shadow-lg" />
                    </div>
                  )}
                </button>
              </div>
            </div>
          </div>
          
          {/* 컴팩트 모드에서 검색창 */}
          {showSearch && (
            <div className="px-4 py-3 border-b border-gray-200 bg-white/95">
              {!isHome && (user?.user_type === 'customer' || user?.user_type === 'vip_customer') && totalCount <= 1 ? (
                <div className="bg-blue-50 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <InformationCircleIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-blue-900">
                        {totalCount === 0 
                          ? '현재 진행 중인 프로젝트가 없습니다.' 
                          : '현재 진행 중인 프로젝트가 1개입니다.'}
                      </p>
                      <p className="text-xs text-blue-700 mt-1">
                        {totalCount === 0 
                          ? '프로젝트가 등록되면 여기에 표시됩니다.' 
                          : '아래에서 프로젝트 내용을 바로 확인하실 수 있습니다.'}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {isHome ? (
                    // 홈에서는 검색 버튼 포함된 디자인 - 컴팩트한 디자인
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 flex items-center pointer-events-none">
                          <MagnifyingGlassIcon className="h-4 w-4 text-blue-500" />
                        </div>
                        <input
                          type="text"
                          placeholder="프로젝트명, 주소로 검색..."
                          value={searchInput}
                          onChange={(e) => setSearchInput(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              if (searchInput.trim()) {
                                navigate(`/projects?search=${encodeURIComponent(searchInput)}`);
                              }
                            }
                          }}
                          className={`w-full pl-10 py-2 bg-gray-50 border border-gray-300 rounded-lg 
                            focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 focus:bg-white
                            transition-all text-sm placeholder-gray-500 ${
                              searchInput ? 'pr-9' : 'pr-3'
                            }`}
                        />
                        {searchInput && (
                          <button
                            onClick={() => setSearchInput('')}
                            className="absolute right-2.5 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-200 rounded transition-colors"
                          >
                            <XMarkIcon className="h-3 w-3 text-gray-400" />
                          </button>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          if (searchInput.trim()) {
                            navigate(`/projects?search=${encodeURIComponent(searchInput)}`);
                          }
                        }}
                        disabled={!searchInput.trim()}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                          searchInput.trim()
                            ? 'bg-blue-500 hover:bg-blue-600 text-white shadow-sm'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        검색
                      </button>
                    </div>
                  ) : (
                    // 프로젝트 리스트에서는 엔터로만 검색 - 컴팩트한 디자인
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 transform -translate-y-1/2 flex items-center pointer-events-none">
                        <MagnifyingGlassIcon className="h-4 w-4 text-blue-500" />
                      </div>
                      <input
                        type="text"
                        placeholder={
                          user?.user_type === 'admin' 
                            ? "프로젝트명, NO.106, 고객명, 이메일, 전화번호로 검색..."
                            : user?.user_type === 'contractor'
                            ? "담당 프로젝트 검색..."
                            : "프로젝트명, 주소로 검색..."
                        }
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        className={`w-full pl-10 py-2 bg-gray-50 border border-gray-300 rounded-lg 
                          focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 focus:bg-white
                          transition-all text-sm placeholder-gray-500 ${
                            searchInput ? 'pr-16' : 'pr-12'
                          }`}
                      />
                      {searchInput && (
                        <button
                          onClick={() => {
                            setSearchInput('');
                            setSearchQuery('');
                          }}
                          className="absolute right-2.5 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-200 rounded transition-colors"
                        >
                          <XMarkIcon className="h-3 w-3 text-gray-400" />
                        </button>
                      )}
                      {/* 검색 힌트 - 검색어가 없을 때만 표시 */}
                      {!searchInput && (
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                          <div className="hidden sm:flex items-center text-xs text-gray-400">
                            <span className="px-1.5 py-0.5 bg-gray-200 rounded text-gray-500 font-mono text-xs">Enter</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* 컴팩트 모드에서 필터 */}
          {showFilters && (
            <div className="px-4 py-3 border-b border-gray-200 bg-white/95">
              <div className="p-4 bg-white border-2 border-blue-500 rounded-xl shadow-lg">
                <div className="relative">
                  <div ref={scrollContainerRef} className="overflow-x-auto scrollbar-hide">
                    <div className="flex gap-2" style={{ minWidth: 'max-content' }}>
                      {statusOptions.map(status => (
                        <button
                          key={status.value}
                          onClick={() => {
                            setFilters(prev => ({ ...prev, status: status.value, page: 1 }));
                          }}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                            filters.status === status.value
                              ? status.color || 'bg-blue-600 text-white'
                              : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {status.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="px-4 py-1 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img 
                src="/images/logo-geongi.png" 
                alt="건기" 
                className="h-5 w-auto cursor-pointer"
                onClick={() => navigate('/')}
              />
              <div className="flex items-baseline cursor-pointer group mt-1.5" onClick={() => navigate('/')}>
                <span className="text-base font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">건기</span>
                <div className="relative ml-0.5">
                  <div className="absolute -top-1 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-600 to-blue-800 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
                  <span className="text-xs font-medium text-gray-600 group-hover:text-blue-700 transition-colors duration-300">프로젝트</span>
                </div>
              </div>
              {user?.user_type === 'vip_customer' && (
                <StarIcon className="h-3.5 w-3.5 text-yellow-400 ml-1.5 mt-1.5" />
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowSearch(!showSearch)}
                className={`p-1.5 rounded-lg transition-all relative ${
                  showSearch 
                    ? 'bg-blue-600 text-white hover:bg-blue-700' 
                    : 'hover:bg-gray-100'
                }`}
              >
                <MagnifyingGlassIcon className={`h-5 w-5 translate-y-0.5 ${
                  showSearch ? 'text-white' : 'text-gray-600'
                }`} />
              </button>
              <NotificationCenter />
              <div className="h-5 w-px bg-gray-200 mx-1"></div>
              <button
                onClick={() => navigate(user?.user_type === 'vip_customer' ? '/vip' : '/profile')}
                className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors relative"
              >
                <UserCircleIcon className="h-5 w-5 text-gray-600" />
                {user?.user_type === 'vip_customer' && (
                  <div className="absolute -top-1 -right-1">
                    <StarIcon className="h-3 w-3 text-yellow-400" />
                  </div>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 검색 바 - 일반 모드에서만 */}
      {showSearch && !isCompact && (
        <div className="px-4 py-3 border-b border-gray-200">
          {!isHome && (user?.user_type === 'customer' || user?.user_type === 'vip_customer') && totalCount <= 1 ? (
            <div className="bg-blue-50 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <InformationCircleIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-900">
                    {totalCount === 0 
                      ? '현재 진행 중인 프로젝트가 없습니다.' 
                      : '현재 진행 중인 프로젝트가 1개입니다.'}
                  </p>
                  <p className="text-xs text-blue-700 mt-1">
                    {totalCount === 0 
                      ? '프로젝트가 등록되면 여기에 표시됩니다.' 
                      : '아래에서 프로젝트 내용을 바로 확인하실 수 있습니다.'}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {isHome ? (
                // 홈에서는 검색 버튼 포함된 디자인 - 컴팩트한 디자인
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2 flex items-center pointer-events-none">
                      <MagnifyingGlassIcon className="h-4 w-4 text-blue-500" />
                    </div>
                    <input
                      type="text"
                      placeholder="프로젝트명, 주소로 검색..."
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          if (searchInput.trim()) {
                            navigate(`/projects?search=${encodeURIComponent(searchInput)}`);
                          }
                        }
                      }}
                      className={`w-full pl-10 py-2 bg-gray-50 border border-gray-300 rounded-lg 
                        focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 focus:bg-white
                        transition-all text-sm placeholder-gray-500 ${
                          searchInput ? 'pr-9' : 'pr-3'
                        }`}
                    />
                    {searchInput && (
                      <button
                        onClick={() => setSearchInput('')}
                        className="absolute right-2.5 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-200 rounded transition-colors"
                      >
                        <XMarkIcon className="h-3 w-3 text-gray-400" />
                      </button>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      if (searchInput.trim()) {
                        navigate(`/projects?search=${encodeURIComponent(searchInput)}`);
                      }
                    }}
                    disabled={!searchInput.trim()}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      searchInput.trim()
                        ? 'bg-blue-500 hover:bg-blue-600 text-white shadow-sm'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    검색
                  </button>
                </div>
              ) : (
                // 프로젝트 리스트에서는 엔터로만 검색 - 컴팩트한 디자인
                <div className="relative">
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="h-4 w-4 text-blue-500" />
                  </div>
                  <input
                    type="text"
                    placeholder={
                      user?.user_type === 'admin' 
                        ? "프로젝트명, NO.106, 고객명, 이메일, 전화번호로 검색..."
                        : user?.user_type === 'contractor'
                        ? "담당 프로젝트 검색..."
                        : "프로젝트명, 주소로 검색..."
                    }
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className={`w-full pl-10 py-2 bg-gray-50 border border-gray-300 rounded-lg 
                      focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 focus:bg-white
                      transition-all text-sm placeholder-gray-500 ${
                        searchInput ? 'pr-16' : 'pr-12'
                      }`}
                  />
                  {searchInput && (
                    <button
                      onClick={() => {
                        setSearchInput('');
                        setSearchQuery('');
                      }}
                      className="absolute right-2.5 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-200 rounded transition-colors"
                    >
                      <XMarkIcon className="h-3 w-3 text-gray-400" />
                    </button>
                  )}
                  {/* 검색 힌트 - 검색어가 없을 때만 표시 */}
                  {!searchInput && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                      <div className="hidden sm:flex items-center text-xs text-gray-400">
                        <span className="px-1.5 py-0.5 bg-gray-200 rounded text-gray-500 font-mono text-xs">Enter</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* 프로젝트 정보 및 필터 */}
      {!isHome && !isCompact && (
        <div className="px-4 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <h2 className="text-sm font-semibold text-gray-900">
                {searchInput ? '검색 결과' : 
                 filters?.status ? `${statusOptions.find(s => s.value === filters.status)?.label} 프로젝트` : '전체 프로젝트'}
              </h2>
              <span className="px-1.5 py-0.5 bg-blue-50 text-blue-600 text-xs font-medium rounded-md">
                {totalCount || 0}건
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setShowFilters(!showFilters);
                  if (showFilters) {
                    setFilters(prev => ({ ...prev, status: '', page: 1 }));
                    const newSearchParams = new URLSearchParams(window.location.search);
                    newSearchParams.delete('filter');
                    const newUrl = newSearchParams.toString() ? `${window.location.pathname}?${newSearchParams.toString()}` : window.location.pathname;
                    window.history.replaceState({}, '', newUrl);
                  }
                }}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-lg transition-all text-sm ${
                  showFilters 
                    ? 'bg-blue-600 text-white hover:bg-blue-700' 
                    : 'bg-gray-100 hover:bg-gray-200'
                }`}
              >
                <FunnelIcon className={`h-4 w-4 transition-transform duration-200 ${showFilters ? 'rotate-180' : ''}`} />
                <span>필터</span>
              </button>
              {showCreateButton && user?.user_type === 'admin' && (
                <button
                  onClick={() => navigate('/admin/projects/create')}
                  className="p-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  <PlusIcon className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>

          {showFilters && !isCompact && (
            <div className="mt-3 p-4 bg-white border-2 border-blue-500 rounded-xl shadow-lg">
              <div className="relative">
                <div ref={scrollContainerRef} className="overflow-x-auto scrollbar-hide">
                  <div className="flex gap-2" style={{ minWidth: 'max-content' }}>
                    {statusOptions.map(status => (
                      <button
                        key={status.value}
                        onClick={() => {
                          setFilters(prev => ({ ...prev, status: status.value, page: 1 }));
                        }}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                          filters.status === status.value
                            ? status.color || 'bg-blue-600 text-white'
                            : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {status.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PageHeader;